import { debounce } from "@mui/material";
import { useMemo, useState, createContext, useContext, useCallback } from "react";

export const StepperEnum = {
    Details: 'Details',
    DataSources: 'DataSource(s)',
    Fields: 'Fields',
    Pivot: 'Pivoting',
    Filters: 'Filters',
    Styles: 'Styles',
    Create: 'Create',
    Update: 'Update'
} as const;

export type TStepperEnum = typeof StepperEnum[keyof typeof StepperEnum];

export interface IWizardStepper {
    isNewReport: boolean;
    steps: string[];
    activeStepIndex: number;
    activeStep: TStepperEnum;
    skippedSteps: number[];
    invalidSteps: number[];
    isStepOptional: (index: number) => boolean;
    isStepSkipped: (index: number) => boolean;
    handleBack: () => void;
    handleSkip: () => void;
    handleNext: () => void;
    handleReset: () => void;
    validateStep: (stepperIndex: number, value: boolean) => void;
    setError: (stepperIndex: number, error: string | null) => void;
    finishTrigger: number;
}

/**
 * @author Pradeep R <pradeep.rajendran@rldatix.com>
 * @createdOn Dec/01/2024
 */
function useWizardStepper(isNewReport: boolean): IWizardStepper {
    const steps = useMemo(() => {
        const baseSteps = [
            StepperEnum.Details,
            StepperEnum.DataSources,
            StepperEnum.Fields,
            StepperEnum.Pivot,
            StepperEnum.Filters,
            StepperEnum.Styles,
        ];
        const finalStep = isNewReport ? StepperEnum.Create : StepperEnum.Update;
        return [...baseSteps, finalStep];
    }, [isNewReport]);

    const [activeStepIndex, setActiveStepIndex] = useState<number>(0);
    const [skippedSteps, setSkippedSteps] = useState<number[]>([]); // Now an array
    const [invalidSteps, setInvalidSteps] = useState<number[]>([]); // Now an array
    const [finishTrigger, setFinishTrigger] = useState<number>(0);

    const activeStep = steps[activeStepIndex];

    const isStepOptional = (index: number) => steps[index] === StepperEnum.Pivot || steps[index] === StepperEnum.Filters;
    const isStepSkipped = (index: number) => skippedSteps.includes(index);

    const validateStep = useCallback(
        debounce((stepperIndex, isValid) => {
            setInvalidSteps((prev) => {
                const isAlreadyValid = !prev.includes(stepperIndex);
                if (isValid && !isAlreadyValid) {
                    return prev.filter((step) => step !== stepperIndex); // Remove invalid step
                } else if (!isValid && isAlreadyValid) {
                    return [...prev, stepperIndex]; // Add invalid step
                }
                return prev; // No change needed
            });
        }, 100),
        []
    );

    const setError = (stepperIndex: number, error: string | null) => {
        const isValid: boolean = error ? false : true;
        validateStep(stepperIndex, isValid);
    }

    const handleNext = () => {
        if (!invalidSteps.includes(activeStepIndex) && activeStepIndex < steps.length - 1) {
            setActiveStepIndex((prevIndex) => prevIndex + 1);
            setSkippedSteps((prevSkipped) => prevSkipped.filter((step) => step !== activeStepIndex));
        }

        // set/reset the finish trigger
        if (activeStep === StepperEnum.Create || activeStep === StepperEnum.Update) {
            setFinishTrigger((prev) => prev + 1);
        } else {
            setFinishTrigger(0);
        }
    };

    const handleBack = () => {
        if (activeStepIndex > 0) {
            setActiveStepIndex((prevIndex) => prevIndex - 1);
        }
    };

    const handleSkip = () => {
        if (activeStepIndex < steps.length - 1) {
            setSkippedSteps((prevSkipped) => [...prevSkipped, activeStepIndex]);
            setActiveStepIndex((prevIndex) => prevIndex + 1);
        }
    };

    const handleReset = () => {
        setActiveStepIndex(0);
        setFinishTrigger(0);
        setSkippedSteps([]);
        setInvalidSteps([]);
    };

    return {
        isNewReport,
        steps,
        activeStepIndex,
        activeStep,
        skippedSteps,
        invalidSteps,
        isStepOptional,
        isStepSkipped,
        handleBack,
        handleSkip,
        handleNext,
        handleReset,
        validateStep,
        setError,
        finishTrigger,
    };
}

export const WizardStepperContext = createContext<IWizardStepper>({
    isNewReport: false,
    steps: [],
    activeStepIndex: 0,
    activeStep: StepperEnum.Details,
    skippedSteps: [],
    invalidSteps: [],
    isStepOptional: () => false,
    isStepSkipped: () => false,
    handleBack: () => { },
    handleSkip: () => { },
    handleNext: () => { },
    handleReset: () => { },
    validateStep: (stepperIndex: number, value: boolean) => { },
    setError: (stepperIndex: number, error: string | null) => { },
    finishTrigger: 0,
});

export const WizardStepperContextProvider = ({
    children,
    isNewReport,
}: {
    children: React.ReactNode;
    isNewReport: boolean;
}) => {
    const wizardStepperHook = useWizardStepper(isNewReport);

    return (
        <WizardStepperContext.Provider value={wizardStepperHook}>
            {children}
        </WizardStepperContext.Provider>
    );
};

export const useWizardStepperContext = () => useContext(WizardStepperContext);